/**
 * The product sync page's tabs, deep-linked through `?tab=` (DESIGN.md
 * section 12.1.1: one parameter name for the view switch, validated, and an
 * unknown value falls back to the default rather than blanking the page).
 *
 * Order on screen: Products, Product updates, Code tables.
 */
export type ProductSyncTab = 'products' | 'updates' | 'code';

const TABS: readonly ProductSyncTab[] = ['products', 'updates', 'code'];

export function parseProductSyncTab(value: string | null | undefined): ProductSyncTab {
  return TABS.find((tab) => tab === value) ?? 'products';
}

/**
 * `?product=` on the Product updates tab: the product to scroll to and
 * highlight once the worklist has folded it (a print-file issue links here for
 * a platen variant size). Consumed once, then cleared.
 */
export const PRODUCT_UPDATES_PRODUCT_PARAM = 'product';

/**
 * The numeric Shopify product id from a gid (`gid://shopify/Product/123`) or a
 * bare id, or null. The worklist keys rows by gid while issues carry the
 * numeric `order_items.product_id`, so both sides compare through this.
 */
export function numericShopifyProductId(id: string | number | null | undefined): string | null {
  if (id === null || id === undefined) return null;
  const match = /(\d+)$/.exec(String(id).trim());
  return match ? match[1]! : null;
}

/** The Product updates tab, focused on one product when its id is known. */
export function productUpdatesHref(productId?: string | number | null): string {
  const numeric = numericShopifyProductId(productId);
  return numeric
    ? `/product-sync?tab=updates&${PRODUCT_UPDATES_PRODUCT_PARAM}=${numeric}`
    : '/product-sync?tab=updates';
}

/** The Code tables tab's Customizer section (BLU-1399): its element id and its deep link (opens it). */
export const CUSTOMIZER_SECTION_ID = 'customizer';
export const CUSTOMIZER_SETTINGS_HREF = '/product-sync?tab=code#customizer';
